import { ReactNode } from "react";
import { ShieldAlert, WifiOff } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../integrations/supabase/client";
import { Loading } from "../components/shared/ui/Loading";
import { EmptyState } from "../components/shared/ui/EmptyState";
import { Button } from "../components/ui/button";
import { useUserProfileData } from "../features/profile/hooks/useUserProfileData";
import { queryKeys } from "../lib/queryKeys";
import { logErrorToConsole } from "../utils/errorHandlerUtils";

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Guard del área autenticada. App.tsx solo lo monta cuando YA hay sesión, así
 * que aquí solo se resuelve el perfil. Importante: cuando el perfil no puede
 * cargarse NO se navega a /login — con sesión activa /login rebota a "/" y se
 * producía un bucle infinito de redirecciones. En su lugar se muestra un estado
 * recuperable (reintentar / cerrar sesión).
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const queryClient = useQueryClient();
  const { userProfile, isLoading, isError } = useUserProfileData();

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      logErrorToConsole(error, { module: "RequireAuth", operation: "signOut" });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] bg-background flex items-center justify-center">
        <Loading message="Verificando autenticación" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4">
        <EmptyState
          icon={WifiOff}
          title="No se pudo cargar tu perfil"
          description="Revisa tu conexión a internet e inténtalo de nuevo."
          action={
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() =>
                  queryClient.invalidateQueries({ queryKey: queryKeys.userProfile() })
                }
              >
                Reintentar
              </Button>
              <Button variant="outline" size="sm" onClick={handleSignOut}>
                Cerrar sesión
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  if (!userProfile) {
    // Sesión válida pero sin fila de perfil: cuenta eliminada o sin acceso.
    return (
      <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4">
        <EmptyState
          icon={ShieldAlert}
          title="Tu cuenta no tiene acceso"
          description="Esta cuenta no tiene un perfil activo en Agricos Monitor. Contacta al administrador."
          action={
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              Cerrar sesión
            </Button>
          }
        />
      </div>
    );
  }

  return <>{children}</>;
}
